import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  Divider,
  Autocomplete,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import PurchaseService from '../../../services/purchaseService';
import { productService } from '../../../services/productService';
import { formatCurrency } from '../../../utils/formatters';

interface PurchaseOrderItemForm {
  variantId: string;
  variantName?: string;
  sku?: string;
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  taxRate: number;
}

interface PurchaseOrderFormData {
  supplierId: string;
  orderDate: string;
  expectedDelivery: string;
  shippingCharges: number;
  packingCharges: number;
  notes: string;
  items: PurchaseOrderItemForm[];
}

const PurchaseOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = Boolean(id);

  // Form state
  const [formData, setFormData] = useState<PurchaseOrderFormData>({
    supplierId: '',
    orderDate: format(new Date(), 'yyyy-MM-dd'),
    expectedDelivery: format(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'), // 14 days from now
    shippingCharges: 0,
    packingCharges: 0,
    notes: '',
    items: []
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [variants, setVariants] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Fetch suppliers and products on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setDataLoading(true);
        
        // Fetch suppliers
        console.log('Fetching suppliers...');
        const suppliersResponse = await PurchaseService.getSuppliers({ page: 1, limit: 100 });
        console.log('Suppliers response:', suppliersResponse);
        const suppliersList = suppliersResponse.items || [];
        console.log('Suppliers list:', suppliersList);
        setSuppliers(suppliersList);

        // Fetch products with variants
        console.log('Fetching products...');
        const productsResponse = await productService.listProducts({ page: 1, per_page: 1000 });
        console.log('Products response:', productsResponse);
        const productsList = productsResponse.items || [];
        console.log('Products list:', productsList);
        setProducts(productsList);
        
        // Extract all variants from products
        const allVariants: any[] = [];
        productsList.forEach((product: any) => {
          if (product.variants && Array.isArray(product.variants)) {
            product.variants.forEach((variant: any) => {
              allVariants.push({
                ...variant,
                productName: product.name,
                hsnCode: product.hsn_code,
                gstRate: product.gst_rate || 18
              });
            });
          }
        });
        console.log('Extracted variants:', allVariants);
        setVariants(allVariants);

        // If editing, fetch existing purchase order
        if (isEditing && id) {
          const poData = await PurchaseService.getPurchaseOrder(id);
          setFormData({
            supplierId: poData.supplierId,
            orderDate: poData.orderDate,
            expectedDelivery: poData.expectedDelivery || '',
            shippingCharges: poData.shippingCharges,
            packingCharges: poData.packingCharges,
            notes: poData.notes || '',
            items: poData.items.map((item: any) => ({
              variantId: item.variantId,
              variantName: item.variant?.name,
              sku: item.variant?.sku,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              discountPercent: item.discountPercent,
              taxRate: item.taxRate
            }))
          });
          
          // Set selected supplier
          const supplier = suppliersResponse.items?.find((s: any) => s.id === poData.supplierId);
          setSelectedSupplier(supplier);
        }
      } catch (error: any) {
        console.error('Failed to fetch data:', error);
        console.error('Error details:', error.response?.data);
        enqueueSnackbar(error.message || 'Failed to load data', { variant: 'error' });
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, [id, isEditing, enqueueSnackbar]);

  // Handle form field changes
  const handleChange = (field: keyof PurchaseOrderFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle supplier selection
  const handleSupplierChange = (supplier: any) => {
    setSelectedSupplier(supplier);
    handleChange('supplierId', supplier?.id || '');
  };

  // Add new item row
  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          variantId: '',
          quantity: 1,
          unitPrice: 0,
          discountPercent: 0,
          taxRate: 18
        }
      ]
    }));
  };

  // Remove item row
  const handleRemoveItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Handle item field changes
  const handleItemChange = (index: number, field: keyof PurchaseOrderItemForm, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i === index) {
          // If variant changed, update related fields
          if (field === 'variantId') {
            const variant = variants.find(v => v.id === value);
            // Use purchase_price for purchase orders, not sale_price or unit_price
            const purchasePrice = variant?.purchase_price || variant?.purchasePrice || 0;
            return {
              ...item,
              variantId: value,
              variantName: variant?.name,
              sku: variant?.sku,
              unitPrice: purchasePrice,
              taxRate: variant?.gstRate || 18
            };
          }
          return { ...item, [field]: value };
        }
        return item;
      })
    }));
  };

  // Calculate line totals
  const calculateLineTotal = (item: PurchaseOrderItemForm) => {
    const lineAmount = item.quantity * item.unitPrice;
    const discountAmount = lineAmount * (item.discountPercent / 100);
    const taxableAmount = lineAmount - discountAmount;
    const taxAmount = taxableAmount * (item.taxRate / 100);
    return taxableAmount + taxAmount;
  };

  // Calculate order totals
  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => {
      const lineAmount = item.quantity * item.unitPrice;
      const discountAmount = lineAmount * (item.discountPercent / 100);
      return sum + (lineAmount - discountAmount);
    }, 0);

    const taxAmount = formData.items.reduce((sum, item) => {
      const lineAmount = item.quantity * item.unitPrice;
      const discountAmount = lineAmount * (item.discountPercent / 100);
      const taxableAmount = lineAmount - discountAmount;
      return sum + (taxableAmount * (item.taxRate / 100));
    }, 0);

    const grandTotal = subtotal + taxAmount + formData.shippingCharges + formData.packingCharges;

    return { subtotal, taxAmount, grandTotal };
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.supplierId) {
      newErrors.supplierId = 'Supplier is required';
    }

    if (!formData.orderDate) {
      newErrors.orderDate = 'Order date is required';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'At least one item is required';
    }

    formData.items.forEach((item, index) => {
      if (!item.variantId) {
        newErrors[`item_${index}_variant`] = 'Product is required';
      }
      if (item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
      }
      if (item.unitPrice < 0) {
        newErrors[`item_${index}_unitPrice`] = 'Unit price cannot be negative';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      enqueueSnackbar('Please fix the errors in the form', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        supplierId: formData.supplierId,
        orderDate: formData.orderDate,
        expectedDelivery: formData.expectedDelivery || undefined,
        shippingCharges: formData.shippingCharges,
        packingCharges: formData.packingCharges,
        notes: formData.notes || undefined,
        items: formData.items.map(item => ({
          variantId: item.variantId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discountPercent: item.discountPercent
        }))
      };

      if (isEditing && id) {
        await PurchaseService.updatePurchaseOrder(id, payload);
        enqueueSnackbar('Purchase order updated successfully', { variant: 'success' });
      } else {
        await PurchaseService.createPurchaseOrder(payload);
        enqueueSnackbar('Purchase order created successfully', { variant: 'success' });
      }

      navigate('/admin/purchase/orders');
    } catch (error: any) {
      console.error('Failed to save purchase order:', error);
      enqueueSnackbar(error.message || 'Failed to save purchase order', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  if (dataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/admin/purchase/orders')}
          sx={{ textDecoration: 'none', color: 'primary.main', cursor: 'pointer' }}
        >
          Purchase Orders
        </Link>
        <Typography color="text.primary">
          {isEditing ? 'Edit Purchase Order' : 'Create Purchase Order'}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/admin/purchase/orders')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            {isEditing ? 'Edit Purchase Order' : 'Create Purchase Order'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => window.open('/admin/products/create', '_blank')}
          >
            Add Product
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => window.open('/admin/parties/suppliers/create', '_blank')}
          >
            Add Supplier
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Purchase Order'}
          </Button>
        </Box>
      </Box>

      {errors.items && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.items}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Supplier and Date Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Supplier & Date Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={suppliers}
                    getOptionLabel={(option) => option.businessName || option.business_name || ''}
                    value={selectedSupplier}
                    onChange={(_, newValue) => handleSupplierChange(newValue)}
                    noOptionsText={suppliers.length === 0 ? "No suppliers found. Please add suppliers first." : "No matching suppliers"}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Supplier *"
                        error={Boolean(errors.supplierId)}
                        helperText={errors.supplierId || (suppliers.length === 0 ? "No suppliers available" : "")}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Order Date *"
                    type="date"
                    value={formData.orderDate}
                    onChange={(e) => handleChange('orderDate', e.target.value)}
                    error={Boolean(errors.orderDate)}
                    helperText={errors.orderDate}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Expected Delivery"
                    type="date"
                    value={formData.expectedDelivery}
                    onChange={(e) => handleChange('expectedDelivery', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {selectedSupplier && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Contact:</strong> {selectedSupplier.contactPerson || selectedSupplier.contact_person || 'N/A'} | 
                        <strong> Phone:</strong> {selectedSupplier.phone} | 
                        <strong> GSTIN:</strong> {selectedSupplier.gstin || 'N/A'} |
                        <strong> Payment Terms:</strong> {selectedSupplier.paymentTerms || selectedSupplier.payment_terms || 30} days
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Items */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Typography variant="h6">
                  Items
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddItem}
                  size="small"
                >
                  Add Item
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="30%">Product *</TableCell>
                      <TableCell width="10%" align="right">Qty *</TableCell>
                      <TableCell width="15%" align="right">Purchase Price *</TableCell>
                      <TableCell width="10%" align="right">Disc %</TableCell>
                      <TableCell width="10%" align="right">Tax %</TableCell>
                      <TableCell width="15%" align="right">Line Total</TableCell>
                      <TableCell width="10%" align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No items added. Click "Add Item" to get started.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      formData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Autocomplete
                              size="small"
                              options={variants}
                              getOptionLabel={(option) => 
                                `${option.productName} - ${option.name} (${option.sku})`
                              }
                              value={variants.find(v => v.id === item.variantId) || null}
                              onChange={(_, newValue) => 
                                handleItemChange(index, 'variantId', newValue?.id || '')
                              }
                              noOptionsText={variants.length === 0 ? "No products found. Please add products first." : "No matching products"}
                              renderOption={(props, option) => (
                                <li {...props}>
                                  <Box sx={{ width: '100%' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {option.productName} - {option.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      SKU: {option.sku} | 
                                      MRP: {formatCurrency(option.mrp || 0)} | 
                                      Sale: {formatCurrency(option.sale_price || option.salePrice || 0)} | 
                                      <strong style={{ color: '#1976d2' }}>Purchase: {formatCurrency(option.purchase_price || option.purchasePrice || 0)}</strong>
                                    </Typography>
                                  </Box>
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder={variants.length === 0 ? "No products available" : "Select product"}
                                  error={Boolean(errors[`item_${index}_variant`])}
                                />
                              )}
                            />
                            {item.variantId && variants.find(v => v.id === item.variantId) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                MRP: {formatCurrency(variants.find(v => v.id === item.variantId)?.mrp || 0)} | 
                                Sale: {formatCurrency(variants.find(v => v.id === item.variantId)?.sale_price || variants.find(v => v.id === item.variantId)?.salePrice || 0)} | 
                                <strong>Purchase: {formatCurrency(variants.find(v => v.id === item.variantId)?.purchase_price || variants.find(v => v.id === item.variantId)?.purchasePrice || 0)}</strong>
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={item.quantity}
                              onChange={(e) => 
                                handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)
                              }
                              error={Boolean(errors[`item_${index}_quantity`])}
                              inputProps={{ min: 0, step: 1 }}
                              sx={{ width: '80px' }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={item.unitPrice}
                              onChange={(e) => 
                                handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)
                              }
                              error={Boolean(errors[`item_${index}_unitPrice`])}
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{ width: '100px' }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={item.discountPercent}
                              onChange={(e) => 
                                handleItemChange(index, 'discountPercent', parseFloat(e.target.value) || 0)
                              }
                              inputProps={{ min: 0, max: 100, step: 0.1 }}
                              sx={{ width: '70px' }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={item.taxRate}
                              onChange={(e) => 
                                handleItemChange(index, 'taxRate', parseFloat(e.target.value) || 0)
                              }
                              inputProps={{ min: 0, max: 100, step: 0.1 }}
                              sx={{ width: '70px' }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatCurrency(calculateLineTotal(item))}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveItem(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Charges and Totals */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={4}
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Add any additional notes or instructions..."
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Shipping Charges"
                    type="number"
                    value={formData.shippingCharges}
                    onChange={(e) => handleChange('shippingCharges', parseFloat(e.target.value) || 0)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Packing Charges"
                    type="number"
                    value={formData.packingCharges}
                    onChange={(e) => handleChange('packingCharges', parseFloat(e.target.value) || 0)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2">{formatCurrency(totals.subtotal)}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Tax Amount:</Typography>
                <Typography variant="body2">{formatCurrency(totals.taxAmount)}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Shipping:</Typography>
                <Typography variant="body2">{formatCurrency(formData.shippingCharges)}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2">Packing:</Typography>
                <Typography variant="body2">{formatCurrency(formData.packingCharges)}</Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Grand Total:</Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(totals.grandTotal)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PurchaseOrderForm;
